import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@fly/core';

import { useGetArtifactVersionsInfiniteQuery } from '../../../../store/registry.service';
import { DistributionForm } from './DistributionForm';
import { type OpenArtifactDistributionDialogParams } from './distributionFormKeys';

export interface ArtifactDistributionDialogContentProps extends OpenArtifactDistributionDialogParams {
  reopenDistributionDialog?: () => void;
}

export function ArtifactDistributionDialogContent({
  artifactName,
  versionName,
  packageType,
  reopenDistributionDialog,
}: ArtifactDistributionDialogContentProps) {
  const { t } = useTranslation();
  const { data, isFetching, isLoading } = useGetArtifactVersionsInfiniteQuery({
    name: artifactName,
    packageType,
    first: 1,
    versionsFirst: 1,
    versionsSearch: versionName,
  });

  const artifactVersion = useMemo(
    () =>
      data?.pages
        .flatMap((page) => page[0]?.versions ?? [])
        .find((version) => version.name === versionName),
    [data, versionName]
  );

  const artifactVersions = artifactVersion
    ? {
        artifactName,
        versionName: artifactVersion.name,
        packageType,
        distributed: artifactVersion.distribution != null,
        distributionUrl:
          artifactVersion.url ??
          artifactVersion.distribution?.distributionUrl ??
          null,
      }
    : undefined;

  if (isLoading || (isFetching && artifactVersions == null)) {
    return (
      <div className='flex min-h-[160px] items-center justify-center px-fly-4'>
        <Text size='2' color='neutral' className='font-semibold'>
          {t('app.dashboard.artifacts.distribution_form.loading', {
            defaultValue: 'Loading distribution options...',
          })}
        </Text>
      </div>
    );
  }

  if (artifactVersions == null) {
    return (
      <div className='flex min-h-[160px] items-center justify-center px-fly-4'>
        <Text size='2' color='neutral' className='font-semibold'>
          {t('app.dashboard.artifacts.distribution_form.not_found', {
            defaultValue: 'Artifact version not found',
          })}
        </Text>
      </div>
    );
  }

  return (
    <DistributionForm
      artifactVersions={[{ ...artifactVersions, isSupported: true }]}
      reopenDistributionDialog={reopenDistributionDialog}
    />
  );
}
